import { useEffect, useState, useCallback } from 'react';
import { AdminSidebar, Icon, StatusBadge, Modal, Pagination, formatBRL, formatDate } from '../../components/shared';
import api from '../../services/api';
import toast from 'react-hot-toast';

const STATUS_OPCOES = ['pendente', 'aceito', 'em_rota', 'entregue', 'retirado', 'cancelado'];

const LABEL_STATUS = {
  pendente:  'Pendente',
  aceito:    'Aceito',
  em_rota:   'Em Rota',
  entregue:  'Entregue',
  retirado:  'Retirado',
  cancelado: 'Cancelado',
};

const LABEL_TIPO = { entrega: 'Entrega', retirada: 'Retirada', mesa: 'Mesa' };
const LABEL_PAG  = { dinheiro: 'Dinheiro', cartao: 'Cartão', pix: 'PIX', online: 'Online' };

export default function AdminPedidos() {
  const [pedidos, setPedidos]   = useState([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [loading, setLoading]   = useState(true);
  const [filtroStatus, setFiltroStatus] = useState('');
  const [dataIni, setDataIni]   = useState('');
  const [dataFim, setDataFim]   = useState('');

  const [detalhe, setDetalhe]   = useState(null);
  const [carregandoDet, setCarregandoDet] = useState(false);
  const [novoStatus, setNovoStatus]       = useState('');
  const [obsStatus, setObsStatus]         = useState('');
  const [salvandoStatus, setSalvandoStatus] = useState(false);
  const [fuso, setFuso] = useState('America/Sao_Paulo');

  useEffect(() => {
    api.get('/configuracoes').then(r => { if (r.data.fuso_horario) setFuso(r.data.fuso_horario); }).catch(() => {});
  }, []);

  const carregar = useCallback(() => {
    setLoading(true);
    api.get('/pedidos', { params: { page, limit: 20, status: filtroStatus, data_ini: dataIni, data_fim: dataFim } })
      .then(r => { setPedidos(r.data.dados); setTotal(r.data.total); })
      .catch(() => toast.error('Erro ao carregar pedidos'))
      .finally(() => setLoading(false));
  }, [page, filtroStatus, dataIni, dataFim]);

  useEffect(() => { carregar(); }, [carregar]);

  const verDetalhe = async (id) => {
    setCarregandoDet(true);
    setDetalhe({ _loading: true });
    try {
      const { data } = await api.get(`/pedidos/${id}`);
      setDetalhe(data);
      setNovoStatus(data.status);
      setObsStatus('');
    } catch {
      toast.error('Erro ao carregar detalhes do pedido');
      setDetalhe(null);
    } finally {
      setCarregandoDet(false);
    }
  };

  const fecharDetalhe = () => { setDetalhe(null); setNovoStatus(''); setObsStatus(''); };

  const atualizarStatus = async () => {
    if (novoStatus === detalhe.status) return toast('Status não foi alterado', { icon: 'ℹ️' });
    setSalvandoStatus(true);
    try {
      await api.patch(`/pedidos/${detalhe.id}/status`, { status: novoStatus, observacao: obsStatus });
      toast.success('Status atualizado!');
      setDetalhe(d => ({ ...d, status: novoStatus }));
      carregar();
      // recarrega detalhes para atualizar histórico
      const { data } = await api.get(`/pedidos/${detalhe.id}`);
      setDetalhe(data);
      setNovoStatus(data.status);
      setObsStatus('');
    } catch {
      toast.error('Erro ao atualizar status');
    } finally {
      setSalvandoStatus(false);
    }
  };

  return (
    <div className="admin-layout">
      <AdminSidebar perfil="admin_empresa" />
      <div className="main-content">
        <div className="topbar"><h1>Pedidos</h1></div>

        <div className="page-content">
          {/* Filtros */}
          <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
            <select
              className="form-control"
              value={filtroStatus}
              onChange={e => { setFiltroStatus(e.target.value); setPage(1); }}
              style={{ width: 180 }}
            >
              <option value="">Todos os status</option>
              {STATUS_OPCOES.map(s => <option key={s} value={s}>{LABEL_STATUS[s]}</option>)}
            </select>
            <input type="date" className="form-control" value={dataIni}
              onChange={e => { setDataIni(e.target.value); setPage(1); }} style={{ width: 160 }} />
            <input type="date" className="form-control" value={dataFim}
              onChange={e => { setDataFim(e.target.value); setPage(1); }} style={{ width: 160 }} />
            {(filtroStatus || dataIni || dataFim) && (
              <button className="btn" onClick={() => { setFiltroStatus(''); setDataIni(''); setDataFim(''); setPage(1); }}>
                Limpar filtros
              </button>
            )}
          </div>

          <div className="table-card">
            <div className="table-header">
              <span style={{ fontSize: '.85rem', color: 'var(--text-muted)' }}>{total} pedido(s)</span>
            </div>

            {loading ? <div className="loading-center"><div className="spinner" /></div> : (
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Cliente</th>
                      <th>Tipo</th>
                      <th>Pagamento</th>
                      <th>Total</th>
                      <th>Status</th>
                      <th>Data/Hora</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pedidos.map(p => (
                      <tr
                        key={p.id}
                        onClick={() => verDetalhe(p.id)}
                        style={{ cursor: 'pointer' }}
                        title="Clique para ver detalhes"
                      >
                        <td style={{ fontSize: '.75rem', color: 'var(--text-muted)', fontFamily: 'monospace' }}>
                          {p.id.slice(0, 8)}…
                        </td>
                        <td style={{ fontWeight: 600 }}>{p.nome_cliente || '—'}</td>
                        <td><span className="badge badge-secondary">{LABEL_TIPO[p.tipo] || p.tipo}</span></td>
                        <td style={{ fontSize: '.85rem' }}>{LABEL_PAG[p.forma_pagamento] || p.forma_pagamento}</td>
                        <td style={{ fontWeight: 700, color: 'var(--primary)' }}>{formatBRL(p.valor_total)}</td>
                        <td><StatusBadge status={p.status} /></td>
                        <td style={{ fontSize: '.8rem', color: 'var(--text-muted)' }}>{formatDate(p.data_hora, fuso)}</td>
                      </tr>
                    ))}
                    {!pedidos.length && (
                      <tr><td colSpan={7}>
                        <div className="empty-state"><Icon name="orders" /><h3>Nenhum pedido encontrado</h3></div>
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            <div className="table-footer">
              <span>Mostrando {pedidos.length} de {total}</span>
              <Pagination page={page} total={total} limit={20} onChange={setPage} />
            </div>
          </div>
        </div>
      </div>

      {/* ── Modal Detalhes ── */}
      <Modal open={!!detalhe} onClose={fecharDetalhe} title="Detalhes do Pedido" size="modal-lg">
        {carregandoDet || detalhe?._loading ? (
          <div className="loading-center" style={{ padding: '40px' }}><div className="spinner" /></div>
        ) : detalhe && (
          <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '20px' }}>

            {/* Cabeçalho */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px' }}>
              <div>
                <div style={{ fontSize: '.75rem', color: 'var(--text-muted)', marginBottom: '2px' }}>
                  Pedido #{detalhe.id?.slice(0, 8).toUpperCase()}
                </div>
                <div style={{ fontSize: '.85rem', color: 'var(--text-muted)' }}>{formatDate(detalhe.data_hora, fuso)}</div>
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <span className="badge badge-secondary">{LABEL_TIPO[detalhe.tipo] || detalhe.tipo}</span>
                <StatusBadge status={detalhe.status} />
              </div>
            </div>

            <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: 0 }} />

            {/* Dados do cliente + entrega */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <div style={{ fontWeight: 700, marginBottom: '8px', fontSize: '.875rem' }}>Cliente</div>
                <InfoLinha label="Nome"     value={detalhe.nome_cliente} />
                <InfoLinha label="Telefone" value={detalhe.telefone} />
                <InfoLinha label="E-mail"   value={detalhe.email} />
                <InfoLinha label="CPF"      value={detalhe.cpf_cliente} />
              </div>
              {detalhe.tipo === 'entrega' && (
                <div>
                  <div style={{ fontWeight: 700, marginBottom: '8px', fontSize: '.875rem' }}>Endereço de Entrega</div>
                  <InfoLinha label="Logradouro" value={detalhe.endereco} />
                  <InfoLinha label="Número"     value={detalhe.numero} />
                  <InfoLinha label="Bairro"     value={detalhe.bairro} />
                  <InfoLinha label="Complemento" value={detalhe.complemento} />
                  <InfoLinha label="Cidade/UF"  value={detalhe.cidade && detalhe.estado ? `${detalhe.cidade} / ${detalhe.estado}` : null} />
                  <InfoLinha label="CEP"        value={detalhe.cep} />
                </div>
              )}
            </div>

            <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: 0 }} />

            {/* Itens */}
            <div>
              <div style={{ fontWeight: 700, marginBottom: '10px', fontSize: '.875rem' }}>Itens do Pedido</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.85rem' }}>
                <thead>
                  <tr style={{ background: 'var(--secondary)', textAlign: 'left' }}>
                    <th style={{ padding: '8px 10px' }}>Produto</th>
                    <th style={{ padding: '8px 10px', textAlign: 'center' }}>Qtd</th>
                    <th style={{ padding: '8px 10px', textAlign: 'right' }}>Unit.</th>
                    <th style={{ padding: '8px 10px', textAlign: 'right' }}>Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {(detalhe.itens || []).map((it, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '8px 10px' }}>
                        <div style={{ fontWeight: 600 }}>{it.descricao_produto}</div>
                        {it.observacao && <div style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>{it.observacao}</div>}
                      </td>
                      <td style={{ padding: '8px 10px', textAlign: 'center' }}>{it.quantidade}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'right' }}>{formatBRL(it.valor_unitario)}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700 }}>{formatBRL(it.valor_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Total e pagamento */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px', padding: '10px 10px 0' }}>
                <span style={{ fontSize: '.85rem', color: 'var(--text-muted)' }}>
                  Pagamento: <strong>{LABEL_PAG[detalhe.forma_pagamento] || detalhe.forma_pagamento}</strong>
                </span>
                <span style={{ fontWeight: 800, fontSize: '1.1rem', color: 'var(--primary)' }}>
                  Total: {formatBRL(detalhe.valor_total)}
                </span>
              </div>

              {detalhe.observacao && (
                <div style={{ marginTop: '10px', padding: '10px', background: 'var(--secondary)', borderRadius: 'var(--radius)', fontSize: '.85rem' }}>
                  <strong>Obs:</strong> {detalhe.observacao}
                </div>
              )}
            </div>

            <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: 0 }} />

            {/* Alterar status */}
            <div>
              <div style={{ fontWeight: 700, marginBottom: '10px', fontSize: '.875rem' }}>Atualizar Status</div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div style={{ flex: '1 1 160px' }}>
                  <label className="form-label" style={{ fontSize: '.8rem' }}>Novo status</label>
                  <select className="form-control" value={novoStatus} onChange={e => setNovoStatus(e.target.value)}>
                    {STATUS_OPCOES.map(s => <option key={s} value={s}>{LABEL_STATUS[s]}</option>)}
                  </select>
                </div>
                <div style={{ flex: '2 1 200px' }}>
                  <label className="form-label" style={{ fontSize: '.8rem' }}>Observação (opcional)</label>
                  <input className="form-control" value={obsStatus} onChange={e => setObsStatus(e.target.value)} placeholder="Ex: saiu para entrega" />
                </div>
                <button
                  className="btn btn-primary"
                  onClick={atualizarStatus}
                  disabled={salvandoStatus || novoStatus === detalhe.status}
                >
                  {salvandoStatus ? 'Salvando...' : 'Atualizar'}
                </button>
              </div>
            </div>

            {/* Histórico */}
            {detalhe.historico?.length > 0 && (
              <>
                <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: 0 }} />
                <div>
                  <div style={{ fontWeight: 700, marginBottom: '10px', fontSize: '.875rem' }}>Histórico de Status</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {detalhe.historico.map((h, i) => (
                      <div key={i} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', fontSize: '.8rem' }}>
                        <div style={{ width: '130px', color: 'var(--text-muted)', flexShrink: 0 }}>{formatDate(h.data_hora, fuso)}</div>
                        <div>
                          {h.status_anterior
                            ? <><StatusBadge status={h.status_anterior} /> → <StatusBadge status={h.status_novo} /></>
                            : <StatusBadge status={h.status_novo} />
                          }
                          {h.usuario_nome && <span style={{ color: 'var(--text-muted)', marginLeft: '6px' }}>por {h.usuario_nome}</span>}
                          {h.observacao && <div style={{ color: 'var(--text-muted)', marginTop: '2px' }}>{h.observacao}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

          </div>
        )}
      </Modal>
    </div>
  );
}

function InfoLinha({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ fontSize: '.82rem', marginBottom: '4px', display: 'flex', gap: '6px' }}>
      <span style={{ color: 'var(--text-muted)', minWidth: '80px' }}>{label}:</span>
      <span style={{ fontWeight: 500 }}>{value}</span>
    </div>
  );
}
